import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { MembershipTypeFormSchemaValues } from 'src/features/membershipType/formSchema';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SwitchFormField from 'src/registry/components/forms/SwitchFormField';
import { WizardStepInfo } from 'src/registry/ui/Wizard';

type Props = {
    form: UseFormReturn<MembershipTypeFormSchemaValues>;
};

export default function AccessStep({ form }: Props) {
    const { formatMessage } = useIntl();

    const {
        allow_custom_space_count_definition,
        has_allowed_spaces_count,
        allow_extra_visitors,
        has_time_restriction,
    } = form.watch();

    return (
        <div className="flex gap-4">
            <div className="flex flex-2 flex-col gap-4">
                <SectionBlock>
                    <SectionBlockTitle className="text-base">
                        {formatMessage(membershipTypeMessages.spaces)}
                    </SectionBlockTitle>
                    <SectionBlockBody className="flex-col">
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="has_allowed_spaces_count"
                            label={formatMessage(membershipTypeMessages.limitSpaces)}
                            description={formatMessage(membershipTypeMessages.limitSpacesDescription)}
                            tooltip={formatMessage(membershipTypeMessages.limitSpacesTooltip)}
                        />
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="allow_custom_space_count_definition"
                            label={formatMessage(membershipTypeMessages.customSpaceCount)}
                            disabled={!has_allowed_spaces_count}
                            description={formatMessage(membershipTypeMessages.customSpaceCountDescription)}
                            tooltip={formatMessage(membershipTypeMessages.customSpaceCountTooltip)}
                        />
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="allow_extra_visitors"
                            label={formatMessage(membershipTypeMessages.extraVisitors)}
                            disabled={!has_allowed_spaces_count}
                            description={formatMessage(membershipTypeMessages.extraVisitorsDescription)}
                            tooltip={formatMessage(membershipTypeMessages.extraVisitorsTooltip)}
                        />
                        <InputFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="max_allowed_spaces_count"
                            type="number"
                            pattern={/^[0-9]*$/}
                            variant="default"
                            required={Boolean(!allow_custom_space_count_definition && has_allowed_spaces_count)}
                            disabled={Boolean(!has_allowed_spaces_count || allow_custom_space_count_definition)}
                            label={formatMessage(membershipTypeMessages.maxAllowedSpaces)}
                            description={formatMessage(membershipTypeMessages.maxAllowedSpacesDescription)}
                            tooltip={formatMessage(membershipTypeMessages.maxAllowedSpacesTooltip)}
                        />
                        <InputFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="max_extra_visitors_count"
                            type="number"
                            pattern={/^[0-9]*$/}
                            variant="default"
                            required={Boolean(allow_extra_visitors)}
                            disabled={!allow_extra_visitors}
                            label={formatMessage(membershipTypeMessages.maxExtraVisitors)}
                            description={formatMessage(membershipTypeMessages.maxExtraVisitorsDescription)}
                            tooltip={formatMessage(membershipTypeMessages.maxExtraVisitorsTooltip)}
                        />
                    </SectionBlockBody>
                    <SectionBlockSeparator />
                    <div className="flex flex-col">
                        <SectionBlockTitle className="text-base">
                            {formatMessage(membershipTypeMessages.allowedEntryExitTime)}
                        </SectionBlockTitle>
                        <SectionBlockSubtitle>
                            {formatMessage(membershipTypeMessages.timeRestrictionDescription)}
                        </SectionBlockSubtitle>
                    </div>
                    <SectionBlockBody className="flex-col">
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="has_time_restriction"
                            label={formatMessage(membershipTypeMessages.timeRestriction)}
                            description={formatMessage(membershipTypeMessages.timeRestrictionAccessDescription)}
                            tooltip={formatMessage(membershipTypeMessages.timeRestrictionAccessTooltip)}
                        />
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="allow_custom_time_restriction"
                            label={formatMessage(membershipTypeMessages.customTimeRestriction)}
                            disabled={!has_time_restriction}
                            description={formatMessage(membershipTypeMessages.customTimeRestrictionDescription)}
                            tooltip={formatMessage(membershipTypeMessages.customTimeRestrictionTooltip)}
                        />
                    </SectionBlockBody>
                    <SectionBlockSeparator />
                    <SectionBlockTitle className="text-base">
                        {formatMessage(membershipTypeMessages.others)}
                    </SectionBlockTitle>
                    <SectionBlockBody className="flex-col">
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="allow_access_over_capacity_limit"
                            label={formatMessage(membershipTypeMessages.allowAccessOverCapacityLimit)}
                            description={formatMessage(
                                membershipTypeMessages.allowAccessOverCapacityLimitDescription,
                            )}
                            tooltip={formatMessage(membershipTypeMessages.allowAccessOverCapacityLimitTooltip)}
                        />
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="allow_access_as_client_outside_restrictions"
                            label={formatMessage(membershipTypeMessages.allowAccessAsClientOutsideRestrictions)}
                            description={formatMessage(
                                membershipTypeMessages.allowAccessAsClientOutsideRestrictionsDescription,
                            )}
                            tooltip={formatMessage(
                                membershipTypeMessages.allowAccessAsClientOutsideRestrictionsTooltip,
                            )}
                        />
                        <SwitchFormField<MembershipTypeFormSchemaValues>
                            form={form}
                            name="contribute_to_available_spaces"
                            label={formatMessage(membershipTypeMessages.contributeToAvailableSpaces)}
                            description={formatMessage(
                                membershipTypeMessages.contributeToAvailableSpacesDescription,
                            )}
                            tooltip={formatMessage(membershipTypeMessages.contributeToAvailableSpacesTooltip)}
                        />
                    </SectionBlockBody>
                </SectionBlock>
            </div>
            <div className="flex-1">
                <WizardStepInfo
                    header={formatMessage(membershipTypeMessages.access)}
                    body={formatMessage(membershipTypeMessages.accessBody)}
                    tip={formatMessage(membershipTypeMessages.accessTip)}
                />
            </div>
        </div>
    );
}
